import React, { useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom';
import { useFormik } from 'formik';
import * as Yup from "yup";
import { ADMIN_ROUTES, EXCEPTION_CODE, MODE, STATUS } from '../../../../utils/constant';
import { useSnackbar } from '../../../../hooks/useSnackBar';
import { HTTP_STATUS } from '../../../../utils/types';
import { IUSC, USCPayload, useVASMasterCategoryService } from '../../../../services/useVASMasterCategoryService';
import USCFormTemplate from '../../../templates/VASMaster/USC/USCForm.template';
import USCAndAttributeFormTemplate from '../../../templates/VASMaster/USC/USCAndAttributeDetailsForm.template';
import ReviewFormTemplate from '../../../templates/VASMaster/USC/ReviewForm.template';
import { USC_FORM_STATE } from './USCCreate.page';
import { createUseStyles } from 'react-jss';

const validationSchema = Yup.object().shape({
    name: Yup.string().max(30, "USC Name cannot be more than 30 characters").required("USC Name is required"),
    description: Yup.string().max(500, "Description cannot be more than 500 characters").required("Description is required"),
    superService: Yup.object().shape({
        id: Yup.number().required('Super Service is required'),
        label: Yup.string().required('Super Service is required')
    }).required('Super Service is required'),
    mainService: Yup.object().shape({
        id: Yup.number().required('Main Service is required'),
        label: Yup.string().required('Main Service is required')
    }).required('Main Service is required'),
    productService: Yup.object().shape({
        id: Yup.number().required('Service Category is required'),
        label: Yup.string().required('Service Category is required')
    }).required('Service Category is required'),
    subAttributeIds: Yup.object()
    .test(
      'hasAtLeastOneKey',
      'Check at least one checkbox',
      value => value && Object.keys(value).length > 0
    )
    .required('Sub Attribute is required'),
});

const useStyles = createUseStyles((theme: any) => ({
    mainHeading: {
        color: theme.palette.textV2.primary.primary950,
    },
}));

const USCUpdatePage: React.FC = () => {
    const classes = useStyles();
    const navigate = useNavigate();
    const { showSnackbar, SnackBarComponent } = useSnackbar();
    const vasMasterCategoryService = useVASMasterCategoryService();
    const params = useParams();
    const uscId = Number(params.id);
    
    const [formState, setFormState] = useState<USC_FORM_STATE>(1);
    
    const [usc, setUsc] = useState<IUSC | null>(null)
    const loadUsc = async () => {
        vasMasterCategoryService.getUSCById(Number(params.id))
            .then(res => {
                if (res.status === HTTP_STATUS.OK) {
                    setUsc(res.data.data)
                }
            }).catch((error) => {
                setUsc(null);
                console.error("Error Fetching USC: ", error);
                showSnackbar('error', "Error while fetching USC data");
            })
    }

    useEffect(() => {
        loadUsc()
    }, [Number(params.id)])

    const onClose = () => {
        navigate(ADMIN_ROUTES.VAS_MASTER_USCS_LIST);
    }

    const updateMainService = async (usc: USCPayload) => {
        const requestBody: any = {
            serviceCategoryId: usc?.productServiceId,
            name: usc?.name,
            description: usc?.description,
            status: usc?.status,
            subAttributeIds: usc?.subAttributeIds
        }
        vasMasterCategoryService.updateUSC(uscId, requestBody)
            .then(res => {
                if (res.status === HTTP_STATUS.OK) {
                    showSnackbar('success', "USC Updated");
                    onClose();
                } else if (res.data.exceptionCode === EXCEPTION_CODE.DUPLICATE_ENTRY) {
                    setFormState(USC_FORM_STATE.USC_DETAILS)
                    showSnackbar('error', "USC already exists. Please try again");
                }
            }).catch((error) => {
                showSnackbar('error', "Failed to update USC");
            })
    }

    const subAttributeIds = usc?.attributes.reduce((acc: any, attribute: any) => {
        attribute.subAttributes.forEach((subAttr: any) => {
            acc[subAttr.id] = {
                minTolerance: 0,
                maxTolerance: 0
            };
        });
        return acc;
    }, {});

    const formik = useFormik<USCPayload>({
        initialValues: {
            name: usc?.name ?? "",
            uscCode: usc?.uscCode ?? "",
            status: usc?.status ?? STATUS.ACTIVE,
            description: usc?.description ?? "",
            productServiceId: usc?.services?.id ?? null,
            superService: {
                label: usc?.services?.superParentService?.name ?? "",
                id: usc?.services?.superParentService?.id ?? ""
            },
            mainService: {
                label: usc?.services?.parentService?.name ?? "",
                id: usc?.services?.parentService?.id ?? ""
            },
            productService: {
                label: usc?.services?.name ?? "",
                id: usc?.services?.name ?? "",
            },
            attributes: usc?.attributes ?? [],
            subAttributeIds: subAttributeIds ?? {}
        },
        validationSchema,
        onSubmit: async (values) => {
            updateMainService({ ...values, status: values.status })
        }
    })

    useEffect(() => {
        formik.setValues({
            name: usc?.name ?? "",
            uscCode: usc?.uscCode ?? "",
            status: usc?.status ?? STATUS.ACTIVE,
            description: usc?.description ?? "",
            productServiceId: usc?.services?.id ?? null,
            superService: {
                label: usc?.services?.ancestors?.superParentService?.name ?? "",
                id: usc?.services?.ancestors?.superParentService?.id ?? ""
            },
            mainService: {
                label: usc?.services?.ancestors?.parentService?.name ?? "",
                id: usc?.services?.ancestors?.parentService?.id ?? ""
            },
            productService: {
                label: usc?.services?.name ?? "",
                id: usc?.services?.id ?? "",
            },
            attributes: usc?.attributes ?? [],
            subAttributeIds: subAttributeIds ?? {}
        });
    }, [usc]);

    const setFormStateTo = (formState: USC_FORM_STATE) => () => {
        setFormState(formState)
    }

    const activeFormStep = useMemo(() => {
        const props = { mode: MODE.UPDATE, onClose, formik, setFormStateTo }
        switch (formState) {
            case USC_FORM_STATE.REVIEW: return <ReviewFormTemplate {...props} />;
            default: return <USCAndAttributeFormTemplate {...props} />;
        }
    }, [formState, formik])

    return (
        <div>
            {SnackBarComponent}
            <div className="grid gap-y-6" >
                <div className={`${classes.mainHeading} text-lg font-semibold`}>Edit USC - ${formik.values?.uscCode}</div>
                <USCFormTemplate {...{ mode: MODE.UPDATE, onClose, component: activeFormStep }} />
            </div>
        </div>
    )
}

export default USCUpdatePage